// Per-stem gang playback (Phase 4, task 25). Plays ALL ready stems of ONE version at once against
// a single shared playhead, with per-stem mute / solo / volume. This is a QUICK-LISTEN monitor,
// not a mixer — the real work happens in the user's DAW. So: no loop, no loudness match, no
// waveform (a lightweight shared playhead bar + click-to-seek is enough).
//
// Audio model (reused from TrackPlayer's proven transport): each stem is its own
//   <audio> → MediaElementAudioSource → GainNode → ctx.destination
// chain. All chains sum at the single shared destination, so N stems play together. Every element
// is kept in lockstep (seek-all / play-all / pause-all); only per-stem gain differs. Gain policy:
// if ANY stem is soloed, only soloed stems are audible; otherwise all non-muted stems are — each
// scaled by its own volume, ramped click-free with setTargetAtTime.

import { Fragment, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { AppConfig } from './config';
import type { Session } from './auth';
import { getPlaybackUrl, type VersionAsset } from './api';
import { Badge, Btn, Icon, Muted, t, ShortcutsHelp, type ShortcutRow } from './ui';
import { useKeyboardShortcuts } from './useKeyboardShortcuts';

/** "44.1 kHz / 24-bit / stereo" style format string for a stem asset. */
function fmtFormat(a: VersionAsset): string {
  const parts: string[] = [];
  if (a.sampleRateHz) parts.push(`${(a.sampleRateHz / 1000).toFixed(1)} kHz`);
  if (a.bitDepth) parts.push(`${a.bitDepth}-bit`);
  if (a.channels) parts.push(a.channels === 1 ? 'mono' : a.channels === 2 ? 'stereo' : `${a.channels}ch`);
  return parts.join(' / ') || '—';
}

interface LoadedStem {
  assetId: string;
  name: string;
  el: HTMLAudioElement;
  src: MediaElementAudioSourceNode;
  gain: GainNode;
  durationSecs: number;
}

interface StemControl {
  muted: boolean;
  soloed: boolean;
  volume: number; // 0..1
}

function fmt(ms: number): string {
  const s = Math.max(0, Math.floor(ms / 1000));
  const m = Math.floor(s / 60);
  const r = s % 60;
  return `${m}:${r.toString().padStart(2, '0')}`;
}

export function StemsPlayer({
  config,
  session,
  versionId,
  assets,
  fileControls,
}: {
  config: AppConfig;
  session: Session;
  projectId: string;
  versionId: string;
  /** The version's assets; only `ready` stems are loaded/playable. */
  assets: VersionAsset[];
  /**
   * Optional file-management controls, supplied by StemsView so the ONE table shows both the
   * playback controls (mute/solo/volume, owned here) and the file controls (select/download/
   * delete, owned by StemsView) on the same row. When omitted, only playback controls render.
   */
  fileControls?: {
    canDownload: boolean;
    selected: Record<string, boolean>;
    onToggleSelect: (assetId: string, checked: boolean) => void;
    canDelete: (a: VersionAsset) => boolean;
    onDelete: (a: VersionAsset) => void;
    onDownload: () => void;
    busy: boolean;
  };
}) {
  // Only ready stems are playable. Memoized so the load effect has a stable dependency.
  const readyStems = useMemo(
    () => assets.filter((a) => a.ingestStatus === 'ready'),
    [assets]
  );
  const readyKey = readyStems.map((a) => a.assetId).join(',');

  const [playing, setPlaying] = useState(false);
  const [ready, setReady] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [playheadMs, setPlayheadMs] = useState(0);
  const [durationMs, setDurationMs] = useState(0);
  const [showHelp, setShowHelp] = useState(false);

  // Per-stem controls keyed by assetId (mute / solo / volume).
  const [controls, setControls] = useState<Record<string, StemControl>>({});
  // Mirror for the gain effect / rAF without forcing rebuilds.
  const controlsRef = useRef(controls);
  useEffect(() => { controlsRef.current = controls; }, [controls]);

  const ctxRef = useRef<AudioContext | null>(null);
  const loadedRef = useRef<Map<string, LoadedStem>>(new Map());
  const playingRef = useRef(false);
  useEffect(() => { playingRef.current = playing; }, [playing]);

  // Stable clock stem: the first loaded stem drives the shared playhead readout.
  const clockAssetId = readyStems[0]?.assetId ?? null;

  const teardown = useCallback(() => {
    for (const l of loadedRef.current.values()) {
      l.el.pause();
      try { l.src.disconnect(); l.gain.disconnect(); } catch { /* already gone */ }
      l.el.src = '';
    }
    loadedRef.current = new Map();
  }, []);

  // --- Build the audio graph for the current set of ready stems. ---
  useEffect(() => {
    if (readyStems.length === 0) { setReady(false); return; }
    let cancelled = false;
    setLoading(true);
    setError(null);

    const build = async () => {
      const ctx = ctxRef.current ?? new AudioContext();
      ctxRef.current = ctx;

      // Remove any stems no longer present.
      const keep = new Set(readyStems.map((a) => a.assetId));
      for (const [id, l] of [...loadedRef.current.entries()]) {
        if (!keep.has(id)) {
          l.el.pause();
          try { l.src.disconnect(); l.gain.disconnect(); } catch { /* gone */ }
          l.el.src = '';
          loadedRef.current.delete(id);
        }
      }

      const toAdd = readyStems.filter((a) => !loadedRef.current.has(a.assetId));
      try {
        await Promise.all(
          toAdd.map(async (asset) => {
            const { url } = await getPlaybackUrl(config, session, versionId, asset.assetId);
            const el = new Audio();
            el.crossOrigin = 'anonymous'; // required or MediaElementSource taints the graph
            el.preload = 'auto';
            el.src = url;
            const src = ctx.createMediaElementSource(el);
            const gain = ctx.createGain();
            gain.gain.value = 0; // the gain effect sets the real target once loaded
            src.connect(gain).connect(ctx.destination);
            await new Promise<void>((resolve, reject) => {
              el.addEventListener('loadedmetadata', () => resolve(), { once: true });
              el.addEventListener('error', () => reject(new Error(`load failed for ${asset.assetId}`)), { once: true });
            });
            if (cancelled) { el.pause(); return; }
            const durationSecs = (asset.durationMs ?? 0) / 1000 || el.duration || 0;
            loadedRef.current.set(asset.assetId, {
              assetId: asset.assetId,
              name: asset.stemName || asset.originalFilename || asset.assetId.slice(0, 8),
              el, src, gain, durationSecs,
            });
          })
        );
        if (cancelled) return;

        // Seed controls for any new stems (default: audible, unmuted, unsoloed, full volume).
        setControls((prev) => {
          const next = { ...prev };
          for (const a of readyStems) {
            if (!next[a.assetId]) next[a.assetId] = { muted: false, soloed: false, volume: 1 };
          }
          // Drop controls for stems no longer present.
          for (const id of Object.keys(next)) if (!keep.has(id)) delete next[id];
          return next;
        });

        // Shared duration = the longest stem (they should all match; longest is the safe span).
        const maxSecs = Math.max(0, ...[...loadedRef.current.values()].map((l) => l.durationSecs));
        setDurationMs(Math.round(maxSecs * 1000));

        // If already playing, bring new stems into lockstep at the current position.
        if (playingRef.current && toAdd.length) {
          const clock = clockAssetId ? loadedRef.current.get(clockAssetId)?.el : undefined;
          const pos = clock?.currentTime ?? 0;
          for (const a of toAdd) {
            const l = loadedRef.current.get(a.assetId);
            if (!l) continue;
            try { l.el.currentTime = pos; } catch { /* not seekable yet */ }
            l.el.play().catch(() => { /* autoplay guard */ });
          }
        }
        setReady(loadedRef.current.size > 0);
      } catch (e) {
        if (!cancelled) setError(String(e));
      } finally {
        if (!cancelled) setLoading(false);
      }
    };
    build();
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [readyKey, config, session, versionId]);

  // --- Apply per-stem gains whenever controls change (no graph rebuild). ---
  // Policy: if any stem is soloed, only soloed stems are audible; else all non-muted stems.
  // Each audible stem's gain = its volume; everything else 0. Click-free ramp.
  useEffect(() => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    const anySolo = Object.values(controls).some((c) => c.soloed);
    const t = ctx.currentTime;
    for (const [id, l] of loadedRef.current.entries()) {
      const c = controls[id] || { muted: false, soloed: false, volume: 1 };
      const audible = anySolo ? c.soloed : !c.muted;
      l.gain.gain.setTargetAtTime(audible ? c.volume : 0, t, 0.015);
    }
  }, [controls, ready]);

  // --- Drive the shared playhead from the clock stem while playing. ---
  useEffect(() => {
    let raf = 0;
    const tick = () => {
      const el = clockAssetId ? loadedRef.current.get(clockAssetId)?.el : undefined;
      if (el) setPlayheadMs(Math.floor(el.currentTime * 1000));
      raf = requestAnimationFrame(tick);
    };
    if (playing) raf = requestAnimationFrame(tick);
    return () => { if (raf) cancelAnimationFrame(raf); };
  }, [playing, clockAssetId]);

  // Tear down on unmount.
  useEffect(() => () => { teardown(); ctxRef.current?.close(); }, [teardown]);

  // Seek every stem together (keeps them aligned).
  const seekAll = useCallback((seconds: number) => {
    for (const l of loadedRef.current.values()) {
      try { l.el.currentTime = seconds; } catch { /* not seekable yet */ }
    }
    setPlayheadMs(Math.floor(seconds * 1000));
  }, []);

  const playPause = async () => {
    const ctx = ctxRef.current;
    if (!ctx) return;
    if (ctx.state === 'suspended') await ctx.resume();
    if (playing) {
      for (const l of loadedRef.current.values()) l.el.pause();
      setPlaying(false);
      return;
    }
    // Align everyone to the clock's position, then start all together.
    const clock = clockAssetId ? loadedRef.current.get(clockAssetId)?.el : undefined;
    const pos = clock?.currentTime ?? 0;
    for (const l of loadedRef.current.values()) {
      try { l.el.currentTime = pos; } catch { /* not seekable yet */ }
    }
    await Promise.all([...loadedRef.current.values()].map((l) => l.el.play()));
    setPlaying(true);
  };

  const stop = () => {
    for (const l of loadedRef.current.values()) l.el.pause();
    seekAll(0);
    setPlaying(false);
  };

  const setStem = (id: string, patch: Partial<StemControl>) =>
    setControls((prev) => ({ ...prev, [id]: { ...(prev[id] || { muted: false, soloed: false, volume: 1 }), ...patch } }));

  // Relative seek by `deltaSecs`, clamped to [0, duration].
  const seekBy = (deltaSecs: number) => {
    const dur = durationMs / 1000;
    if (dur <= 0) return;
    seekAll(Math.min(Math.max(playheadMs / 1000 + deltaSecs, 0), dur));
  };

  // Keyboard shortcuts (armed once stems are ready). Spacebar = play/pause; arrows seek
  // (Shift = fine); Home/End jump; '?' toggles help; Esc closes it.
  useKeyboardShortcuts({
    Space: () => { playPause(); },
    ArrowRight: (e) => seekBy(e.shiftKey ? 1 : 5),
    ArrowLeft: (e) => seekBy(e.shiftKey ? -1 : -5),
    Home: () => seekAll(0),
    End: () => { if (durationMs > 0) seekAll(durationMs / 1000); },
    Slash: (e) => { if (e.shiftKey) setShowHelp((v) => !v); },
    Escape: () => { if (showHelp) setShowHelp(false); },
  }, ready);

  // Click on the playhead bar → seek proportionally.
  const onBarClick = (e: React.MouseEvent<HTMLDivElement>) => {
    if (durationMs <= 0) return;
    const rect = e.currentTarget.getBoundingClientRect();
    const frac = Math.min(1, Math.max(0, (e.clientX - rect.left) / rect.width));
    seekAll((frac * durationMs) / 1000);
  };

  // Keyboard seek on the playhead bar (a11y): arrows nudge, Home/End jump. Left/Right = ±5s,
  // Up/Down = ±1s, PageUp/PageDown = ±10s. Keeps the bar operable without a mouse.
  const onBarKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (durationMs <= 0) return;
    const cur = playheadMs / 1000;
    const dur = durationMs / 1000;
    let next: number | null = null;
    switch (e.key) {
      case 'ArrowRight': next = cur + 5; break;
      case 'ArrowLeft': next = cur - 5; break;
      case 'ArrowUp': next = cur + 1; break;
      case 'ArrowDown': next = cur - 1; break;
      case 'PageUp': next = cur + 10; break;
      case 'PageDown': next = cur - 10; break;
      case 'Home': next = 0; break;
      case 'End': next = dur; break;
      default: return;
    }
    e.preventDefault();
    seekAll(Math.min(Math.max(next, 0), dur));
  };

  const anySolo = Object.values(controls).some((c) => c.soloed);
  const pct = durationMs > 0 ? Math.min(100, (playheadMs / durationMs) * 100) : 0;

  // When embedded in StemsView's version Card (fileControls present), drop the own border/margin
  // so we don't double-box; standalone use keeps its own bordered panel.
  const embedded = !!fileControls;
  const wrapStyle: React.CSSProperties = embedded
    ? {}
    : { border: `1px solid ${t.border}`, borderRadius: t.radius, padding: 12, marginTop: 12 };

  if (readyStems.length === 0) {
    return (
      <div style={{ ...(embedded ? {} : { border: `1px solid ${t.border}`, borderRadius: t.radius, padding: 12, marginTop: 12 }), color: t.muted, fontSize: 13 }}>
        No ready stems to play yet.
      </div>
    );
  }

  return (
    <div style={wrapStyle}>
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 8 }}>
        <strong style={{ fontSize: 14 }}>Play stems</strong>
        <span style={{ fontSize: 12, color: t.muted }}>quick listen — solo/mute to check parts</span>
      </div>

      {/* Transport + shared playhead */}
      <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 10 }}>
        <Btn
          variant="primary"
          onClick={playPause}
          disabled={!ready}
          icon={playing ? <Icon.pause /> : <Icon.play />}
          iconOnly
          aria-label={playing ? 'Pause' : 'Play'}
          title={playing ? 'Pause' : 'Play'}
        />
        <Btn variant="secondary" onClick={stop} disabled={!ready} icon={<Icon.stop />} iconOnly aria-label="Stop" title="Stop" />
        <div
          onClick={onBarClick}
          onKeyDown={onBarKeyDown}
          role="slider"
          tabIndex={ready ? 0 : -1}
          aria-label="Seek position"
          aria-valuemin={0}
          aria-valuemax={Math.floor(durationMs / 1000)}
          aria-valuenow={Math.floor(playheadMs / 1000)}
          aria-valuetext={`${fmt(playheadMs)} of ${fmt(durationMs)}`}
          style={{
            flex: 1, height: 10, background: t.surface2, borderRadius: 5, cursor: 'pointer', position: 'relative',
          }}
          title="Click or use arrow keys to seek"
        >
          <div style={{ position: 'absolute', left: 0, top: 0, bottom: 0, width: `${pct}%`, background: t.accent, borderRadius: 5 }} />
        </div>
        <span style={{ fontSize: 12, color: t.muted, fontVariantNumeric: 'tabular-nums', minWidth: 84, textAlign: 'right' }}>
          {fmt(playheadMs)} / {fmt(durationMs)}
        </span>
      </div>

      {loading && <p style={{ fontSize: 12, color: t.muted }}>Loading stems…</p>}
      {error && <p style={{ fontSize: 12, color: t.danger }}>{error}</p>}

      {/* One unified table: playback controls (mute/solo/volume) live here; the file controls
          (select/delete + format/length/status) are supplied by StemsView via `fileControls` so
          the whole set of a stem's controls sits on a single row. Every stem is listed; only
          `ready` ones are in the audio graph and get live playback controls. */}
      <table style={{ width: '100%', borderCollapse: 'collapse' }}>
        <thead>
          <tr style={{ textAlign: 'left', borderBottom: `1px solid ${t.border}`, fontSize: 12, color: t.muted }}>
            {fileControls?.canDownload && <th style={{ padding: '4px 8px 4px 0', width: 24 }}></th>}
            <th style={{ padding: '4px 8px 4px 0' }}>Stem</th>
            <th style={{ width: 44, textAlign: 'center' }} title="Mute">M</th>
            <th style={{ width: 44, textAlign: 'center' }} title="Solo">S</th>
            <th style={{ width: 130 }}>Level</th>
            {fileControls && <th>Format</th>}
            {fileControls && <th>Length</th>}
            {fileControls && <th>Status</th>}
            {fileControls && <th></th>}
          </tr>
        </thead>
        <tbody>
          {assets.map((a) => {
            const c = controls[a.assetId] || { muted: false, soloed: false, volume: 1 };
            const isReady = a.ingestStatus === 'ready';
            const dimmed = isReady ? (anySolo ? !c.soloed : c.muted) : true;
            const name = a.stemName || a.originalFilename || a.assetId.slice(0, 8);
            const errs = a.techChecks.filter((tc) => tc.severity === 'error');
            const warns = a.techChecks.filter((tc) => tc.severity === 'warn');
            // Base cells: Stem, Mute, Solo, Level = 4. + checkbox (canDownload) + 4 file cols.
            const colCount = 4 + (fileControls?.canDownload ? 1 : 0) + (fileControls ? 4 : 0);
            return (
              <Fragment key={a.assetId}>
                <tr style={{ borderBottom: a.techChecks.length ? 'none' : `1px solid ${t.border}`, opacity: dimmed ? 0.55 : 1 }}>
                  {fileControls?.canDownload && (
                    <td style={{ padding: '6px 8px 6px 0' }}>
                      <input
                        type="checkbox"
                        checked={!!fileControls.selected[a.assetId]}
                        disabled={!isReady}
                        onChange={(e) => fileControls.onToggleSelect(a.assetId, e.target.checked)}
                        title={isReady ? 'Select for download' : 'Not ready'}
                      />
                    </td>
                  )}
                  <td style={{ padding: '6px 8px 6px 0', fontSize: 13 }}>
                    {name}
                    {errs.length > 0 && <span title="format mismatch" style={{ marginLeft: 6, color: t.danger }}>⚑</span>}
                    {warns.length > 0 && <span title="warning" style={{ marginLeft: 4, color: t.warn }}>⚠</span>}
                    {a.uploadedByEmail && <span style={{ marginLeft: 6, fontSize: 11, color: t.faint }}>· {a.uploadedByEmail}</span>}
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Btn
                      variant="ghost"
                      onClick={() => setStem(a.assetId, { muted: !c.muted })}
                      disabled={!isReady}
                      aria-pressed={c.muted}
                      icon={<Icon.mute />}
                      iconOnly
                      style={{ color: c.muted ? t.danger : t.muted }}
                      title={`Mute ${name}`}
                      aria-label={`Mute ${name}`}
                    />
                  </td>
                  <td style={{ textAlign: 'center' }}>
                    <Btn
                      variant="ghost"
                      onClick={() => setStem(a.assetId, { soloed: !c.soloed })}
                      disabled={!isReady}
                      aria-pressed={c.soloed}
                      icon={<Icon.solo />}
                      iconOnly
                      style={{ color: c.soloed ? t.accent : t.muted }}
                      title={`Solo ${name}`}
                      aria-label={`Solo ${name}`}
                    />
                  </td>
                  <td>
                    <input
                      type="range" min={0} max={1} step={0.01} value={c.volume}
                      disabled={!isReady}
                      onChange={(e) => setStem(a.assetId, { volume: Number(e.target.value) })}
                      style={{ width: 120 }}
                      title={`Volume for ${name}`}
                      aria-label={`Volume for ${name}`}
                    />
                  </td>
                  {fileControls && (
                    <td style={{ color: t.muted, fontSize: 13 }}>{fmtFormat(a)}</td>
                  )}
                  {fileControls && (
                    <td style={{ color: t.muted, fontSize: 13 }}>
                      {a.durationMs != null ? `${(a.durationMs / 1000).toFixed(1)}s` : '—'}
                    </td>
                  )}
                  {fileControls && (
                    <td>
                      <Badge tone={isReady ? 'ok' : a.ingestStatus === 'failed' ? 'danger' : 'warn'}>{a.ingestStatus}</Badge>
                    </td>
                  )}
                  {fileControls && (
                    <td style={{ textAlign: 'right' }}>
                      {fileControls.canDelete(a) && (
                        <Btn
                          variant="danger"
                          onClick={() => fileControls.onDelete(a)}
                          disabled={fileControls.busy}
                          icon={<Icon.x />}
                          iconOnly
                          title="Delete this stem"
                          aria-label={`Delete stem ${name}`}
                        />
                      )}
                    </td>
                  )}
                </tr>
                {a.techChecks.length > 0 && (
                  <tr style={{ borderBottom: `1px solid ${t.border}` }}>
                    <td colSpan={colCount} style={{ padding: '0 0 8px 0' }}>
                      {a.techChecks.map((tc, i) => (
                        <div key={i} style={{ fontSize: 12, color: tc.severity === 'error' ? t.danger : t.warn, paddingLeft: 4 }}>
                          {tc.severity === 'error' ? '⚑' : '⚠'} {tc.message}
                        </div>
                      ))}
                    </td>
                  </tr>
                )}
              </Fragment>
            );
          })}
        </tbody>
      </table>

      {/* Download action (only when file controls are present + something is downloadable). */}
      {fileControls?.canDownload && assets.some((a) => a.ingestStatus === 'ready') && fileControls.onDownload && (
        <div style={{ marginTop: 10, display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
          <Btn variant="secondary" icon={<Icon.download />} onClick={fileControls.onDownload} disabled={fileControls.busy}>
            Download selected
          </Btn>
          <Muted>Tick stems above, then download the original files.</Muted>
        </div>
      )}

      <ShortcutsHelp open={showHelp} onClose={() => setShowHelp(false)} rows={STEMS_SHORTCUTS} />
    </div>
  );
}

// Shown in the '?' help overlay for the stems player.
const STEMS_SHORTCUTS: ShortcutRow[] = [
  { keys: ['Space'], label: 'Play / pause' },
  { keys: ['←'], label: 'Back 5 seconds' },
  { keys: ['→'], label: 'Forward 5 seconds' },
  { keys: ['Shift', '←'], label: 'Back 1 second' },
  { keys: ['Shift', '→'], label: 'Forward 1 second' },
  { keys: ['Home'], label: 'Jump to start' },
  { keys: ['End'], label: 'Jump to end' },
  { keys: ['?'], label: 'Show this help' },
  { keys: ['Esc'], label: 'Close help' },
];
